/**
 * Adaptador sobre la puerta del sitio (`/api/site-access` en @sica/backend).
 *
 * Es la única parte de @sica/site que habla con el backend. El código de
 * acceso no vive acá ni en ningún otro archivo del sitio: el bundle es público,
 * así que la comparación pasa entera del lado del servidor.
 *
 * No lanza excepciones — devuelve un resultado discriminado, para que la UI
 * pueda distinguir "código equivocado" de "no pudimos consultar" y no muestre
 * el mensaje equivocado cuando el que falla es el backend.
 */

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

const url = (path: string): string => `${API_BASE}/api/site-access${path}`;

export type AccessAttempt =
  | { status: 'granted'; token: string }
  | { status: 'rejected' }
  | { status: 'unavailable' };

/** Canjea el código por un token de desbloqueo. */
export async function redeemAccessCode(code: string): Promise<AccessAttempt> {
  try {
    const response = await fetch(url('/verify'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });

    if (response.status === 401) return { status: 'rejected' };
    if (!response.ok) return { status: 'unavailable' };

    const body = (await response.json()) as { token?: unknown };
    if (typeof body.token !== 'string' || body.token.length === 0) {
      return { status: 'unavailable' };
    }

    return { status: 'granted', token: body.token };
  } catch {
    return { status: 'unavailable' };
  }
}

/**
 * Revalida contra el backend un token ya guardado. Se consulta en cada carga
 * en vez de confiar en lo que quedó en el navegador: así rotar el código
 * vuelve a cerrar la puerta para todos.
 */
export async function isAccessTokenValid(token: string): Promise<boolean> {
  try {
    const response = await fetch(url('/session'), {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.ok;
  } catch {
    return false;
  }
}
